/**
 * Copy button that yields the external-facing (public) URL of a package,
 * so internal users can hand the link straight to suppliers.
 */

import { useState } from 'react';
import { Button, Tooltip, message } from 'antd';
import { CopyOutlined, CheckOutlined } from '@ant-design/icons';
import { copyToClipboard } from '@/utils';
import {
  buildExternalCurrentUrl,
  buildExternalPackageUrl,
  buildExternalReleaseUrl,
} from '@/utils/externalLink';

interface CopyExternalLinkButtonProps {
  /**
   * 资源 id；kind 为 'current' 时不需要，链接直接取自地址栏。
   */
  id?: number | string;
  /**
   * 'package' = 底软交付物，'release' = Momenta 释放产物包，
   * 'current' = 当前地址栏地址（需求/设计材料/OPL，内容随浏览位置变化）。
   */
  kind?: 'package' | 'release' | 'current';
  projectId?: number | string | null;
  /** 阻止事件冒泡，避免在可点击的表格行中触发行为 */
  stopPropagation?: boolean;
}

export const CopyExternalLinkButton = ({
  id,
  kind = 'package',
  projectId,
  stopPropagation = true,
}: CopyExternalLinkButtonProps) => {
  const [copied, setCopied] = useState(false);
  const [hoverUrl, setHoverUrl] = useState('');

  // 'current' 必须在点击时才求值：地址栏会随用户展开不同需求/问题而变化，
  // 渲染期算好的链接会复制出旧地址。
  const resolveUrl = () => {
    if (kind === 'current') return buildExternalCurrentUrl();
    return kind === 'release'
      ? buildExternalReleaseUrl(id ?? '', projectId)
      : buildExternalPackageUrl(id ?? '', projectId);
  };

  const handleCopy = async (e: React.MouseEvent) => {
    if (stopPropagation) {
      e.stopPropagation();
      e.preventDefault();
    }
    try {
      await copyToClipboard(resolveUrl());
      setCopied(true);
      message.success('外部访问链接已复制');
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      message.error('复制失败，请手动复制');
    }
  };

  return (
    <Tooltip
      // 在浮层弹出时才解析链接：'current' 模式下地址栏会随浏览位置变化，
      // 渲染期算好的链接会过期；这里保证提示里的地址与实际复制结果始终一致。
      onOpenChange={(open) => {
        if (open) setHoverUrl(resolveUrl());
      }}
      title={copied ? '已复制' : `复制外部访问链接：${hoverUrl || resolveUrl()}`}
    >
      <Button
        type="text"
        size="small"
        icon={copied ? <CheckOutlined /> : <CopyOutlined />}
        onClick={handleCopy}
        aria-label="复制外部访问链接"
        className={`bsp-copy-link-btn${copied ? ' bsp-copy-link-btn--copied' : ''}`}
        style={{ marginLeft: 4, verticalAlign: 'middle' }}
      />
    </Tooltip>
  );
};

export default CopyExternalLinkButton;
